import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { API, useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import ProductImage from "@/components/ProductImage";
import { Leaf, ShieldCheck, Truck, CalendarBlank, MapPin, ArrowLeft, Plus, Minus } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Product() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(25);
  const [zooming, setZooming] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const { user, authHeaders } = useAuth();
  const { format, code } = useCurrency();
  const nav = useNavigate();

  useEffect(() => {
    axios.get(`${API}/products/${id}`, { headers: authHeaders(), withCredentials: true })
      .then((r) => setP(r.data))
      .catch(() => setP(null));
  }, [id, authHeaders]);

  if (!p) return <div className="min-h-screen pt-32 text-center text-muted">Loading...</div>;

  const addToCart = async () => {
    if (!user) { toast.error("Sign in as buyer"); return nav("/login"); }
    if (user.role !== "buyer") { toast.error("Buyers only"); return; }
    try {
      await axios.post(`${API}/cart/add`, { product_id: p.id, qty_kg: qty }, { headers: authHeaders(), withCredentials: true });
      toast.success(`Added ${qty}kg to cart`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="pdp-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <Link to="/shop" className="text-muted text-sm hover:text-saffron inline-flex items-center gap-2" data-testid="back-to-shop"><ArrowLeft size={14} /> Back to marketplace</Link>

        <div className="mt-8 grid md:grid-cols-12 gap-10">
          {/* Gallery */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative aspect-square rounded-3xl overflow-hidden bg-ink-800 cursor-zoom-in"
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setZoomPos({
                  x: ((e.clientX - r.left) / r.width) * 100,
                  y: ((e.clientY - r.top) / r.height) * 100,
                });
              }}
              onMouseEnter={() => setZooming(true)}
              onMouseLeave={() => setZooming(false)}
            >
              <img
                src={p.images?.[img]}
                alt={p.branded_name}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-full h-full object-cover transition-transform duration-200"
                style={{
                  transform: zooming ? `scale(2.2)` : "scale(1)",
                  transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`,
                }}
                data-testid="pdp-main-image"
              />
              <div className="absolute top-4 left-4 right-4 flex flex-wrap gap-2 pointer-events-none">
                <span className="chip bg-ink-950/70 backdrop-blur-sm">Grade {p.grade}</span>
                {p.organic && <span className="chip bg-forest/40 border-forest/60 text-cream flex items-center gap-1 backdrop-blur-sm"><Leaf size={10} weight="fill" /> Organic</span>}
                {p.fairtrade && <span className="chip bg-ink-950/70 backdrop-blur-sm">Fairtrade</span>}
              </div>
            </motion.div>
            {(p.images?.length || 0) > 1 && (
              <div className="mt-4 flex gap-3">
                {p.images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setImg(i)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border ${i === img ? "border-saffron" : "border-white/10"}`}
                    data-testid={`thumb-${i}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="md:col-span-5">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="section-label flex items-center gap-2"><MapPin size={12} /> {p.country} · {p.category}</p>
              <h1 className="font-display text-4xl md:text-5xl mt-3 tracking-tight leading-[1.0]" data-testid="product-title">{p.branded_name}</h1>
              <div className="mt-4 flex items-baseline gap-3">
                <span className="font-display text-5xl text-saffron">{format(p.price_usd)}</span>
                <span className="text-muted">/kg {code}</span>
              </div>
              <p className="text-muted mt-6 leading-relaxed">{p.description}</p>

              <div className="mt-8 glass rounded-2xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-cream/80">Quantity (kg)</span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setQty(Math.max(1, qty - 5))} className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:border-saffron" data-testid="qty-minus"><Minus size={14} /></button>
                    <input type="number" value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-20 bg-transparent text-center text-2xl font-display text-cream outline-none" data-testid="qty-input" />
                    <button onClick={() => setQty(qty + 5)} className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:border-saffron" data-testid="qty-plus"><Plus size={14} /></button>
                  </div>
                </div>
                <div className="h-px bg-white/5" />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-cream/80">Order total</span>
                  <span className="font-display text-3xl text-gradient-gold">{format(qty * p.price_usd)}</span>
                </div>
                <button onClick={addToCart} className="btn-primary w-full justify-center" data-testid="add-to-cart-btn">
                  Add to cart
                </button>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-3 text-sm">
                <Spec icon={ShieldCheck} label="Grade" val={p.grade} />
                <Spec icon={Package} label="Stock" val={`${Math.round(p.stock_kg)} kg`} />
                <Spec icon={CalendarBlank} label="Expiry" val={p.expiry || "—"} />
                <Spec icon={Truck} label="Shipping" val={p.shipping_terms || "FOB Origin"} />
              </div>

              {p.certifications?.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {p.certifications.map((c) => (<span key={c} className="chip">{c}</span>))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Package(props) { return <svg width={props.size || 18} height={props.size || 18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 7l9-4 9 4v10l-9 4-9-4V7z"/><path d="M3 7l9 4 9-4M12 11v10"/></svg>; }

function Spec({ icon: Icon, label, val }) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted">{label}</div>
      <div className="flex items-center gap-2 mt-2 text-cream">
        <Icon size={16} weight="duotone" className="text-gold" />
        <span>{val}</span>
      </div>
    </div>
  );
}
